import { ReactNode, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { 
  LayoutDashboard, 
  Package, 
  Truck, 
  Users, 
  Settings, 
  LogOut,
  Menu,
  X,
  Receipt,
  Map,
  FileText,
  Headphones,
  UserPlus

} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import logo from '@/assets/logo.svg';
import logoFull from '@/assets/logo-full.svg';
import { NotificationCenter } from '@/components/NotificationCenter';
import { LanguageSelector } from '@/components/LanguageSelector';
import { useIsMobile } from '@/hooks/use-mobile';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SupportContactDialog } from '@/components/SupportContactDialog';
import { SupportChatFAB } from '@/components/support/SupportChatFAB';
import { AppFooter } from '@/components/layout/AppFooter';

interface AppLayoutProps {
  children: ReactNode;
}

const getNavigationForRole = (role: string | undefined, t: any) => {
  if (role === 'admin') {
    return [
      { name: t('admin.nav.dashboard'), href: '/admin/dashboard', icon: LayoutDashboard },
      { name: t('admin.nav.users'), href: '/admin/users', icon: Users },
      
      { name: t('admin.nav.orders'), href: '/admin/orders', icon: Package },
      { name: t('admin.nav.invoices'), href: '/admin/invoices', icon: Receipt },
      { name: 'Documents', href: '/admin/documents', icon: FileText },
      { name: t('supportChat.adminNav'), href: '/admin/support', icon: Headphones },
      { name: t('nav.settings'), href: '/settings', icon: Settings },
    ];
  }

  if (role === 'transporter') {
    return [
      { name: t('nav.dashboard'), href: '/transporter/dashboard', icon: LayoutDashboard },
      { name: t('nav.marketplace'), href: '/transporter/marketplace', icon: Package },
      { name: t('nav.missions'), href: '/transporter/missions', icon: Package },
      { name: t('nav.liveMap'), href: '/transporter/live-map', icon: Map },
      { name: t('nav.fleet'), href: '/transporter/vehicles', icon: Truck },
      { name: t('nav.billing'), href: '/transporter/billing', icon: Receipt },
      { name: t('nav.settings'), href: '/settings', icon: Settings },
    ];
  }

  if (role === 'shipper') {
    return [
      { name: t('nav.dashboard'), href: '/shipper/dashboard', icon: LayoutDashboard },
      { name: t('nav.myOrders'), href: '/shipper/orders', icon: Package },
      { name: t('nav.liveMap'), href: '/shipper/live-map', icon: Map },
      { name: t('nav.billing'), href: '/shipper/billing', icon: Receipt },
      { name: t('nav.settings'), href: '/settings', icon: Settings },
    ];
  }

  if (role === 'driver') {
    return [
      { name: t('nav.missions'), href: '/driver/missions', icon: Package },
      { name: t('nav.settings'), href: '/settings', icon: Settings },
    ];
  }

  // Default navigation
  return [
    { name: t('nav.dashboard'), href: '/dashboard', icon: LayoutDashboard },
    { name: t('nav.myOrders'), href: '/shipments', icon: Package },
    { name: t('nav.fleet'), href: '/fleet', icon: Truck },
    { name: 'Users', href: '/users', icon: Users },
    { name: t('nav.settings'), href: '/settings', icon: Settings },
  ];
};

const AppLayout = ({ children }: AppLayoutProps) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const navigation = getNavigationForRole(user?.role, t);
  const showSupportButton = user?.role === 'shipper' || user?.role === 'transporter' || user?.role === 'driver';

  // Fetch user profile for avatar
  const { data: profile } = useQuery({
    queryKey: ['user-profile-header', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('users')
        .select('avatar_url, first_name')
        .eq('id', user?.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Auto-collapse sidebar on tablet/mobile
  useEffect(() => {
    if (window.innerWidth < 1024) {
      setSidebarCollapsed(true);
    }
  }, []);

  return (
    <div className="flex min-h-screen w-full bg-background">
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Drawer on mobile, fixed on desktop */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-50 h-screen border-r border-border bg-card transition-all duration-300',
          'lg:z-40',
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          sidebarCollapsed ? 'w-64 lg:w-16' : 'w-64'
        )}
      >
        {/* Logo + Close button on mobile */}
        <div className={cn(
          "flex h-16 items-center border-b border-border",
          sidebarCollapsed && !mobileMenuOpen ? "justify-center px-2" : "justify-between px-4"
        )}>
          {sidebarCollapsed && !mobileMenuOpen ? (
            <img src={logo} alt="Velox" className="h-8 w-8" />
          ) : (
            <img src={logoFull} alt="Velox" className="h-8" />
          )}
          {mobileMenuOpen && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(false)}
              className="lg:hidden"
            >
              <X className="h-5 w-5" />
            </Button>
          )}
        </div>

        {/* Navigation */}
        <TooltipProvider delayDuration={0}>
          <nav className={cn(
            "flex flex-col gap-1",
            sidebarCollapsed && !mobileMenuOpen ? "p-2" : "p-3"
          )}>
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              const isCollapsed = sidebarCollapsed && !mobileMenuOpen;
              
              const linkContent = (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    'flex items-center rounded-lg text-sm font-medium transition-colors min-h-[44px]',
                    isCollapsed ? 'justify-center w-11 h-11 mx-auto' : 'gap-3 px-4 py-3',
                    isActive
                      ? 'bg-secondary text-secondary-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground active:bg-muted/80'
                  )}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  {!isCollapsed && <span>{item.name}</span>}
                </Link>
              );

              if (isCollapsed) {
                return (
                  <Tooltip key={item.name}>
                    <TooltipTrigger asChild>
                      {linkContent}
                    </TooltipTrigger>
                    <TooltipContent side="right" className="font-medium">
                      {item.name}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return linkContent;
            })}
          </nav>
        </TooltipProvider>

        {/* Logout Button */}
        <div className={cn(
          "absolute bottom-0 left-0 right-0 border-t border-border",
          sidebarCollapsed && !mobileMenuOpen ? "p-2" : "p-3"
        )}>
          <TooltipProvider delayDuration={0}>
            {sidebarCollapsed && !mobileMenuOpen ? (
              <div className="flex flex-col gap-2 items-center">
                {showSupportButton && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-11 h-11 text-[#0880AE] hover:text-[#076A8E] hover:bg-[#EBF4F8]"
                        onClick={() => setSupportOpen(true)}
                      >
                        <Headphones className="h-5 w-5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="font-medium">
                      {t('support.callVelox')}
                    </TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-11 h-11 text-muted-foreground hover:text-foreground active:bg-muted/80"
                      onClick={logout}
                    >
                      <LogOut className="h-5 w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="font-medium">
                    {t('common.logout')}
                  </TooltipContent>
                </Tooltip>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                {showSupportButton && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-3 text-[#0880AE] hover:text-[#076A8E] hover:bg-[#EBF4F8] min-h-[44px]"
                    onClick={() => setSupportOpen(true)}
                  >
                    <Headphones className="h-5 w-5" />
                    <span>{t('support.callVelox')}</span>
                  </Button>
                )}
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground active:bg-muted/80 min-h-[44px]"
                  onClick={logout}
                >
                  <LogOut className="h-5 w-5" />
                  <span>{t('common.logout')}</span>
                </Button>
              </div>
            )}
          </TooltipProvider>

        </div>
      </aside>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border safe-area-pb">
        <div className="flex items-center justify-around h-16">
          {navigation.slice(0, 4).map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  'flex flex-col items-center justify-center gap-1 px-3 py-2 flex-1 transition-colors min-h-[44px] active:bg-muted/50',
                  isActive
                    ? 'text-secondary'
                    : 'text-muted-foreground'
                )}
              >
                <item.icon className="h-6 w-6" />
                <span className="text-xs font-medium">{item.name}</span>
              </Link>
            );
          })}
          {showSupportButton && (
            <Button
              variant="ghost"
              className="flex flex-col items-center justify-center gap-1 px-3 py-2 flex-1 text-[#0880AE] min-h-[44px] active:bg-muted/50"
              onClick={() => setSupportOpen(true)}
            >
              <Headphones className="h-6 w-6" />
              <span className="text-xs font-medium">{t('support.callVelox')}</span>
            </Button>
          )}
          <Button
            variant="ghost"
            className="flex flex-col items-center justify-center gap-1 px-3 py-2 flex-1 text-muted-foreground min-h-[44px] active:bg-muted/50"
            onClick={logout}
          >
            <LogOut className="h-6 w-6" />
            <span className="text-xs font-medium">{t('common.logout')}</span>
          </Button>

        </div>
      </nav>

      {/* Main Content */}
      <div
        className={cn(
          'flex-1 transition-all duration-300 w-full',
          'lg:ml-16',
          !sidebarCollapsed && 'lg:ml-64'
        )}
      >
        {/* Header - Mobile: Hamburger + Notification; Desktop: Toggle left, User+Notif right */}
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-card px-4 lg:px-6">
          {/* Mobile: Hamburger + Title */}
          <div className="flex items-center gap-3 lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(true)}
              className="min-h-[44px] min-w-[44px]"
            >
              <Menu className="h-6 w-6" />
            </Button>
            <div>
              <p className="text-sm font-medium">{user?.name || user?.email}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
            </div>
          </div>

          {/* Desktop: Toggle button on left */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden lg:flex min-h-[44px] min-w-[44px]"
          >
            <Menu className="h-5 w-5" />
          </Button>

          {/* Mobile: Notification on right */}
          <div className="lg:hidden">
            <NotificationCenter />
          </div>

          {/* Desktop: Language + User Info + Notification on right */}
          <div className="hidden lg:flex items-center gap-4">
            <LanguageSelector />
            <NotificationCenter />
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-sm font-medium">{user?.name || user?.email}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
              </div>
              <Avatar className="h-9 w-9">
                {profile?.avatar_url ? (
                  <AvatarImage src={profile.avatar_url} alt="Profile" className="object-cover" />
                ) : (
                  <AvatarFallback className="bg-secondary text-secondary-foreground font-semibold">
                    {profile?.first_name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                )}
              </Avatar>
            </div>
          </div>
        </header>

        {/* Page Content - No horizontal padding on mobile, extra bottom padding for mobile nav */}
        <main className="p-0 pb-20 lg:p-6 lg:pb-6">{children}</main>
        <AppFooter variant="app" className="mb-16 lg:mb-0" />
      </div>

      <SupportContactDialog open={supportOpen} onOpenChange={setSupportOpen} />
      <SupportChatFAB />
    </div>
  );
};


export default AppLayout;
